import { useEffect, useState, useCallback } from 'react';
import { erc20Abi, formatUnits, isAddress, isAddressEqual, parseEther, zeroAddress } from 'viem';

import { ETH_DEPOSIT_GAS_LIMIT, getDepositFees } from 'src/utils/crypto/depositFees';

// -------------------------
// Token type helpers (unchanged logic, kept module-level so they are not
// re-created each render and do not need to be effect dependencies).
// -------------------------
function isEthToken(token) {
  const ticker = String(token?.ticker || '').toLowerCase();
  const coingeckoId = String(token?.coingeckoId || '').toLowerCase();
  const address = token?.address;

  if (ticker === 'eth' || ticker === 'ethereum') return true;
  if (coingeckoId === 'ethereum') return true;

  // Only treat zero address as ETH if address actually exists.
  if (typeof address === 'string' && isAddress(address)) {
    return isAddressEqual(address, zeroAddress);
  }

  return false;
}

function isErc20Token(token) {
  return token?.type === 'crypto' && !isEthToken(token);
}

// -------------------------
// Gas reserve for native-ETH deposits.
// Gas limit and fees come from depositFees.js — the same values useDeposit
// sends — and are re-exported here so TransferView's pre-send check uses them
// too. MetaMask requires value + gasLimit * maxFeePerGas in the wallet before
// it will sign, so the reserve is based on the limit, not actual usage.
// -------------------------
export const ETH_DEPOSIT_GAS_UNITS = ETH_DEPOSIT_GAS_LIMIT;

// MAX reserves 150% of the live fee; the pre-send check requires 120%.
// The gap absorbs fee ticks between tapping MAX and tapping Send.
const MAX_RESERVE_FEE_MULTIPLIER = 150n;

// Used only if fee estimation fails entirely.
const ETH_GAS_RESERVE_FALLBACK_WEI = parseEther('0.0005');

export async function getMaxFeePerGas(publicClient) {
  const { maxFeePerGas } = await getDepositFees(publicClient);
  return maxFeePerGas;
}

async function getEthGasReserveWei(publicClient) {
  try {
    const feePerGas = await getMaxFeePerGas(publicClient);
    return (ETH_DEPOSIT_GAS_UNITS * feePerGas * MAX_RESERVE_FEE_MULTIPLIER) / 100n;
  } catch (error) {
    return ETH_GAS_RESERVE_FALLBACK_WEI;
  }
}

// -------------------------
// CHANGE: Retry tunables. On iOS, the MetaMask in-app WebView re-injects
// window.ethereum across background/foreground cycles, and the dRPC transport
// (batch:false, retryCount:1, no fallback) can momentarily reject before the
// provider is live. A short bounded retry with backoff absorbs these transient
// failures instead of surfacing "failed to fetch token balance" on the first miss.
// -------------------------
const BALANCE_FETCH_RETRIES = 3;
const BALANCE_FETCH_RETRY_BASE_MS = 600;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function useTokenOneMax({ publicClient, address, tokenOne }) {
  const [maxAmount, setMaxAmount] = useState('');
  const [loadingMax, setLoadingMax] = useState(false);

  // -------------------------
  // CHANGE: Derive PRIMITIVE values from tokenOne to use as effect dependencies.
  // The original effect depended on the `tokenOne` object directly. Parent
  // components almost always pass a fresh object reference each render (e.g. the
  // selected token is rebuilt from a list), so the effect re-fired on every
  // render — bursting the rate-limited dRPC endpoint and causing the iOS
  // failures. Depending on the primitives means the effect only re-runs when a
  // value that actually changes the query changes.
  // -------------------------
  const tokenType = tokenOne?.type ?? null;
  const tokenAddress = tokenOne?.address ?? null;
  const tokenTicker = tokenOne?.ticker ?? null;
  const tokenCoingeckoId = tokenOne?.coingeckoId ?? null;
  const tokenDecimals = tokenOne?.decimals ?? null;

  // -------------------------
  // CHANGE: A single read attempt, wrapped so the effect can retry it. Returns
  // the formatted balance string, or throws on transport failure so the retry
  // loop can catch and back off.
  // -------------------------
  const readBalanceOnce = useCallback(
    async (token, signal) => {
      if (isEthToken(token)) {
        const [balanceWei, reserveWei] = await Promise.all([
          publicClient.getBalance({ address }),
          getEthGasReserveWei(publicClient),
        ]);
        if (signal.cancelled) return null;

        let safeWei = 0n;
        if (balanceWei > reserveWei) {
          safeWei = balanceWei - reserveWei;
        }
        return formatUnits(safeWei, Number(tokenDecimals ?? 18));
      }

      if (isErc20Token(token)) {
        if (!token.address || !isAddress(token.address)) {
          // This is a data error, not a transport error — do NOT retry it.
          const err = new Error(`Missing ERC-20 address for ${token?.ticker || 'token'}`);
          err.fatal = true;
          throw err;
        }

        const balance = await publicClient.readContract({
          address: token.address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [address],
        });
        if (signal.cancelled) return null;

        return formatUnits(balance, Number(tokenDecimals ?? 18));
      }

      return '';
    },
    [publicClient, address, tokenDecimals]
  );

  useEffect(() => {
    // Mutable signal object shared with the async read so a late-resolving
    // request from a previous run can detect it has been superseded.
    const signal = { cancelled: false };

    const fetchUserTokenBalance = async () => {
      if (!tokenOne || tokenType === 'fiat') {
        setMaxAmount('');
        return;
      }

      // -------------------------
      // CHANGE: Guard against firing before the client AND its transport are
      // usable. On iOS the publicClient reference can exist while the underlying
      // injected provider is mid-reinjection; bailing here (rather than firing a
      // doomed request) avoids a guaranteed failure + needless rate-limit hit.
      // -------------------------
      if (!publicClient || !address) {
        setMaxAmount('');
        return;
      }

      setLoadingMax(true);

      let lastError = null;

      for (let attempt = 1; attempt <= BALANCE_FETCH_RETRIES; attempt += 1) {
        if (signal.cancelled) return;

        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await readBalanceOnce(tokenOne, signal);

          if (signal.cancelled) return;

          if (result !== null) {
            setMaxAmount(result);
          }

          setLoadingMax(false);
          return;
        } catch (error) {
          lastError = error;

          // Fatal (data) errors should not be retried — bail immediately.
          if (error?.fatal) break;

          // Transient transport/network error: back off and retry, unless this
          // was the last attempt.
          if (attempt < BALANCE_FETCH_RETRIES && !signal.cancelled) {
            // eslint-disable-next-line no-await-in-loop
            await sleep(BALANCE_FETCH_RETRY_BASE_MS * attempt);
          }
        }
      }

      if (!signal.cancelled) {
        console.error('[useTokenOneMax] failed to fetch token balance', {
          error: lastError,
          attempts: BALANCE_FETCH_RETRIES,
          tokenTicker,
          address,
        });
        setMaxAmount('');
        setLoadingMax(false);
      }
    };

    fetchUserTokenBalance();

    return () => {
      signal.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    publicClient,
    address,
    tokenType,
    tokenAddress,
    tokenTicker,
    tokenCoingeckoId,
    tokenDecimals,
    readBalanceOnce,
  ]);

  return { maxAmount, loadingMax };
}
